import React, { useState, useRef, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import axios from "../context/axios"; // Adjust path as needed

const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previousQuery, setPreviousQuery] = useState("");
  const [previousTours, setPreviousTours] = useState([]);
  const chatRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const newMessages = [...messages, { sender: "user", text: userInput }];
    setMessages(newMessages);
    setUserInput("");
    setLoading(true);

    try {
      const res = await axios.post("/chatbot", {
        query: userInput,
        previousQuery: messages.length > 1 ? previousQuery : "",
        previousTours: messages.length > 1 ? previousTours : [],
      });
      if (res.data.tours && res.data.tours.length > 0) {
        setMessages([
          ...newMessages,
          { sender: "ai", text: res.data.tours, isTours: true },
        ]);
        setPreviousQuery(res.data.originalQuery || userInput);
        setPreviousTours(res.data.tours);
      } else {
        setMessages([
          ...newMessages,
          { sender: "ai", text: res.data.reply || "No relevant answer found." },
        ]);
      }
    } catch (err) {
      console.error("AI backend error:", err);
      setMessages([
        ...newMessages,
        { sender: "ai", text: "Sorry, something went wrong." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, loading]);

  return (
    <>
      {/* 💬 Chat Toggle Button */}
      <button
        onClick={() => {
          setIsOpen(true);
          if (messages.length === 0) {
            setMessages([
              { sender: "ai", text: "Hello, how can I h you today?" },
            ]);
          }
        }}
        className="fixed bottom-6  right-6 bg-gradient-to-br from-blue-600 to-blue-800 hover:opacity-90 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-2xl z-50 hover:scale-105 transition-transform"
        title="Ask AI Assistant"
        style={{ zIndex: 9999 }}
      >
        💬
      </button>

      {/* 🧠 AI Chat Modal */}
      <Dialog
        open={isOpen}
        // onClose={() => setIsOpen(false)}
        onClose={() => {
          setIsOpen(false);
          setMessages([]);
        }}
        className="fixed inset-0 z-[9999]" // ensures the backdrop and modal wrapper are at highest z
      >
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        <div className="fixed bottom-20 right-6 w-full max-w-sm rounded-xl overflow-hidden shadow-lg bg-white">
          <div className="flex flex-col h-[500px]">
            {/* 🧠 Header */}
            <div className="bg-blue-600 text-white px-4 py-3 text-lg font-semibold flex justify-between items-center">
              <span>TripNest AI Assistant</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMessages([])}
                  title="Clear History"
                  className="text-white hover:text-gray-200 text-sm"
                >
                  🗑️
                </button>

                <button onClick={() => setIsOpen(false)}>✖</button>
              </div>
            </div>

            {/* 📬 Messages */}
            <div
              ref={chatRef}
              className="flex-1 p-3 space-y-3 overflow-y-auto bg-gray-50"
            >
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`text-sm max-w-[85%] p-3 rounded-lg ${
                    msg.sender === "user"
                      ? "ml-auto bg-blue-100 text-right"
                      : "mr-auto bg-gray-200 text-left"
                  }`}
                >
                  {msg.sender === "ai" && msg.isTours ? (
                    <div className="grid gap-4">
                      {msg.text.map((tour, idx) => (
                        <div
                          key={idx}
                          className="bg-white shadow-md rounded-md overflow-hidden border border-gray-200"
                        >
                          <img
                            src={tour.img}
                            alt={tour.title}
                            className="w-full h-36 object-cover"
                            onError={(e) =>
                              (e.target.src = "/default-tour.jpg")
                            }
                          />
                          <div className="p-3">
                            <h3 className="text-md font-bold truncate">
                              {tour.title}
                            </h3>
                            <p className="text-sm text-gray-500">
                              ₹{tour.price} • {tour.duration?.day}D /{" "}
                              {tour.duration?.night}N
                            </p>
                            <p className="text-xs text-gray-700 line-clamp-2 mt-1">
                              {tour.introduction || tour.description}
                            </p>
                            <a
                              href={`/tours/${tour._id}`}
                              className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                            >
                              View Details →
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>{msg.text}</p>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-center items-center py-3">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>

            {/* 📝 Input Area */}
            <form
              onSubmit={handleSubmit}
              className="flex gap-2 border-t border-gray-200 bg-white p-3"
            >
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Ask something like 'Paris adventure tour'"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
              >
                Send
              </button>
            </form>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AIChatWidget;

